<template>
  <div>
    <!-- step indicator (mobile-only via CSS) -->
    <div
      class="sp-step-indicator"
      :aria-label="'שלב ' + signupStep + ' מתוך 2'"
    >
      <span
        class="sp-step-dot"
        :class="{ 'sp-step-dot--active': signupStep >= 1 }"
        aria-hidden="true"
      >1</span>
      <span class="sp-step-line" aria-hidden="true"></span>
      <span
        class="sp-step-dot"
        :class="{ 'sp-step-dot--active': signupStep >= 2 }"
        aria-hidden="true"
      >2</span>
    </div>

    <!-- Generic server error banner -->
    <div
      v-if="signupBanner"
      class="sp-banner sp-banner--error"
      role="alert"
      aria-live="assertive"
    >{{ signupBanner }}</div>

    <!-- ======== STEP 1 ======== -->
    <div v-show="signupStep === 1">
      <form @submit.prevent="advanceToStep2()">
        <ul class="frmUL">

          <!-- שם פרטי -->
          <li class="sp-field">
            <label class="sp-label" for="signup-first_name">
              שם פרטי
              <span class="sp-required" aria-hidden="true">*</span>
            </label>
            <span class="field">
              <input
                id="signup-first_name"
                type="text"
                v-model="signup.first_name"
                aria-required="true"
                :aria-invalid="String(!!signupErrors.first_name)"
                :aria-describedby="signupErrors.first_name ? 'err-first_name' : null"
                @blur="touchField('first_name')"
              />
            </span>
            <span
              v-if="signupErrors.first_name"
              id="err-first_name"
              class="sp-error"
              role="alert"
            >{{ signupErrors.first_name }}</span>
          </li>

          <!-- שם משפחה -->
          <li class="sp-field">
            <label class="sp-label" for="signup-last_name">
              שם משפחה
              <span class="sp-required" aria-hidden="true">*</span>
            </label>
            <span class="field">
              <input
                id="signup-last_name"
                type="text"
                v-model="signup.last_name"
                aria-required="true"
                :aria-invalid="String(!!signupErrors.last_name)"
                :aria-describedby="signupErrors.last_name ? 'err-last_name' : null"
                @blur="touchField('last_name')"
              />
            </span>
            <span
              v-if="signupErrors.last_name"
              id="err-last_name"
              class="sp-error"
              role="alert"
            >{{ signupErrors.last_name }}</span>
          </li>

          <!-- כתובת מייל -->
          <li class="sp-field">
            <label class="sp-label" for="signup-email">
              כתובת מייל
              <span class="sp-required" aria-hidden="true">*</span>
            </label>
            <span class="field">
              <input
                id="signup-email"
                type="email"
                v-model="signup.email"
                aria-required="true"
                :aria-invalid="String(!!signupErrors.email)"
                :aria-describedby="signupErrors.email ? 'err-email' : null"
                @blur="touchField('email')"
              />
            </span>
            <span
              v-if="signupErrors.email"
              id="err-email"
              class="sp-error"
              role="alert"
            >{{ signupErrors.email }}</span>
          </li>

          <!-- טלפון נייד -->
          <li class="sp-field">
            <label class="sp-label" for="signup-phone">
              טלפון נייד
              <span class="sp-required" aria-hidden="true">*</span>
            </label>
            <span class="field">
              <input
                id="signup-phone"
                type="tel"
                v-model="signup.phone"
                aria-required="true"
                :aria-invalid="String(!!signupErrors.phone)"
                :aria-describedby="signupErrors.phone ? 'err-phone' : null"
                @blur="touchField('phone')"
              />
            </span>
            <span
              v-if="signupErrors.phone"
              id="err-phone"
              class="sp-error"
              role="alert"
            >{{ signupErrors.phone }}</span>
          </li>

          <!-- סיסמה + eye-toggle -->
          <li class="sp-field">
            <label class="sp-label" for="signup-password">
              סיסמה
              <span class="sp-required" aria-hidden="true">*</span>
            </label>
            <span class="field sp-field__password-wrap">
              <input
                id="signup-password"
                :type="showPassword ? 'text' : 'password'"
                v-model="signup.password"
                aria-required="true"
                :aria-invalid="String(!!signupErrors.password)"
                :aria-describedby="signupErrors.password ? 'err-password' : null"
                @blur="touchField('password')"
              />
              <button
                type="button"
                class="sp-eye-btn"
                :aria-label="showPassword ? 'הסתר סיסמה' : 'הצג סיסמה'"
                @click="showPassword = !showPassword"
              >
                <svg v-if="showPassword" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                  <line x1="1" y1="1" x2="23" y2="23"/>
                </svg>
                <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
              </button>
            </span>
            <span
              v-if="signupErrors.password"
              id="err-password"
              class="sp-error"
              role="alert"
            >{{ signupErrors.password }}</span>
          </li>

          <!-- תנאי שימוש -->
          <li class="sp-field sp-field--checkbox">
            <label class="sp-checkbox-label" for="signup-terms">
              <input
                id="signup-terms"
                type="checkbox"
                v-model="signup.accept_terms"
                aria-required="true"
                :aria-invalid="String(!!signupErrors.terms)"
                :aria-describedby="signupErrors.terms ? 'err-terms' : null"
                @change="touchField('terms')"
              />
              <span>
                אני מאשר/ת את
                <a href="/terms" target="_blank" rel="noopener noreferrer">תנאי השימוש</a>
                ומדיניות הפרטיות
              </span>
            </label>
            <span v-if="signupErrors.terms" id="err-terms" class="sp-error" role="alert">
              יש לאשר את תנאי השימוש כדי להמשיך
            </span>
          </li>

          <!-- עדכונים ומבצעים -->
          <li class="sp-field sp-field--checkbox">
            <label class="sp-checkbox-label" for="signup-adv">
              <input
                id="signup-adv"
                type="checkbox"
                v-model="signup.accept_adv"
              />
              <span>אני מעוניין/ת לקבל עדכונים ומבצעים במייל</span>
            </label>
          </li>

          <!-- Submit -->
          <li class="sbm_area sp-field">
            <UiButton
              type="submit"
              variant="primary"
              size="lg"
              :block="true"
              :disabled="!isStep1Valid || signupLoading || plansLoading"
              :loading="plansLoading"
            >
              המשך
            </UiButton>
            <span v-if="plansFetchError" class="sp-error" role="alert" style="margin-top:8px;display:block;">
              שגיאה בטעינת המסלולים. נסה/י שוב.
            </span>
          </li>
        </ul>
      </form>

      <div class="sp-switch-link">
        כבר יש לך חשבון?
        <button type="button" class="sp-text-btn" @click="$emit('switch-tab', 'login')">
          התחבר
        </button>
      </div>
    </div>
    <!-- end step 1 -->

    <!-- ======== STEP 2: plan picker (pre-payment — no account exists yet) ======== -->
    <div v-show="signupStep === 2">
      <transition name="sp-view-fade" mode="out-in">

        <!-- ---- plans sub-view ---- -->
        <div v-if="step2View === 'plans'" key="plans">
          <!-- Subscription section heading -->
          <div class="sp-step2-header">
            <h2 class="sp-step2-title">בחר/י את המסלול המתאים לך</h2>
            <p class="sp-step2-subtitle">גישה מלאה לכל התכנים במחיר שמתאים לך</p>
          </div>

          <div v-if="plansFetchError" class="sp-banner sp-banner--error" role="alert">
            לא הצלחנו לטעון את המסלולים כרגע.
            <button type="button" class="sp-inline-retry" @click="retryPlansFetch">נסה/י שוב</button>
          </div>

          <div v-if="plansLoading" class="sp-loading-state">טוען מסלולים...</div>

          <ul class="sp-plans sp-plans--duo" role="radiogroup" aria-label="בחירת מסלול">

            <!-- Paid plans only — no free tier -->
            <li v-for="plan in plans" :key="plan.id">
              <label class="sp-plan-card" :class="{ 'sp-plan-card--selected': selectedPlan === plan.id, 'sp-plan-card--popular': plan.period === 'yearly' }">
                <input
                  type="radio"
                  name="signup_plan"
                  :value="plan.id"
                  v-model="selectedPlan"
                  class="sp-plan-radio"
                />
                <span class="sp-plan-content">
                  <span class="sp-plan-header">
                    <span class="sp-plan-name">{{ plan.name || 'מסלול' }}</span>
                    <span v-if="plan.free_days > 0 || plan.discount_months > 0" class="sp-plan-badges">
                      <span v-if="plan.free_days > 0" class="sp-badge sp-badge--trial">
                        ניסיון {{ plan.free_days }} ימים
                      </span>
                      <span v-if="plan.discount_months > 0" class="sp-badge sp-badge--discount">
                        מבצע {{ plan.discount_months }} חודשים
                      </span>
                    </span>
                  </span>
                  <span class="sp-plan-price-wrap">
                    <span class="sp-plan-price-main">
                      ₪{{ effectivePrice(plan) || 0 }}
                    </span>
                    <span v-if="planPeriodLabel(plan)" class="sp-plan-price-period">{{ planPeriodLabel(plan) }}</span>
                    <span v-if="plan.discount_months > 0 && Number(plan.price) > Number(effectivePrice(plan))" class="sp-plan-price-original">
                      ₪{{ plan.price }}
                    </span>
                    <span v-if="plan.period === 'yearly' && yearlySavingsPct(plan) > 0" class="sp-plan-savings">
                      חיסכון {{ yearlySavingsPct(plan) }}% לעומת חודשי
                    </span>
                  </span>
                  <p class="sp-plan-desc">
                    {{ plan.description || 'צפה בכל התכנים ללא הגבלה בכל מכשיר ובכל זמן.' }}
                  </p>
                  <ul class="sp-plan-benefits">
                    <li v-for="(benefit, idx) in planBenefits(plan)" :key="idx">{{ benefit }}</li>
                  </ul>
                </span>
              </label>
            </li>

          </ul>

          <div v-if="!plansLoading && !plansFetchError && plans.length === 0" class="sp-empty-state">
            אין מסלולים זמינים כרגע. נסה/י שוב מאוחר יותר.
          </div>

          <UiButton
            type="button"
            variant="primary"
            size="lg"
            :block="true"
            :disabled="signupLoading || selectedPlan === null || selectedPlan === undefined || selectedPlan === ''"
            :loading="signupLoading"
            @click="submitSignup()"
          >
            המשך לתשלום
          </UiButton>

          <div class="sp-unit-link">
            <button type="button" class="sp-text-btn" @click="openUnitMemberForm">לחברי היחידה</button>
          </div>
        </div>

        <!-- ---- unit-member lead form sub-view ---- -->
        <div v-else-if="step2View === 'unitForm'" key="unitForm">
          <button type="button" class="sp-back-link" @click="backToPlans">
            <span class="sp-back-link__icon" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </span>
            חזרה למסלולים
          </button>

          <div class="sp-step2-header">
            <h2 class="sp-step2-title">לחברי היחידה</h2>
            <p class="sp-step2-subtitle">השאירו פרטים ונחזור אליכם בהקדם</p>
          </div>

          <div v-if="unitLeadBanner" class="sp-banner sp-banner--error" role="alert" aria-live="assertive">{{ unitLeadBanner }}</div>

          <form @submit.prevent="submitUnitLead()">
            <ul class="frmUL">
              <li class="sp-field">
                <label class="sp-label" for="unit-name">
                  שם מלא
                  <span class="sp-required" aria-hidden="true">*</span>
                </label>
                <span class="field">
                  <input
                    id="unit-name"
                    type="text"
                    v-model="unitLead.name"
                    aria-required="true"
                    :aria-invalid="String(!!unitLeadErrors.name)"
                    :aria-describedby="unitLeadErrors.name ? 'err-unit-name' : null"
                    @blur="touchUnitField('name')"
                  />
                </span>
                <span v-if="unitLeadErrors.name" id="err-unit-name" class="sp-error" role="alert">{{ unitLeadErrors.name }}</span>
              </li>

              <li class="sp-field">
                <label class="sp-label" for="unit-phone">
                  טלפון נייד
                  <span class="sp-required" aria-hidden="true">*</span>
                </label>
                <span class="field">
                  <input
                    id="unit-phone"
                    type="tel"
                    v-model="unitLead.phone"
                    aria-required="true"
                    :aria-invalid="String(!!unitLeadErrors.phone)"
                    :aria-describedby="unitLeadErrors.phone ? 'err-unit-phone' : null"
                    @blur="touchUnitField('phone')"
                  />
                </span>
                <span v-if="unitLeadErrors.phone" id="err-unit-phone" class="sp-error" role="alert">{{ unitLeadErrors.phone }}</span>
              </li>

              <li class="sp-field">
                <label class="sp-label" for="unit-email">
                  כתובת מייל
                  <span class="sp-required" aria-hidden="true">*</span>
                </label>
                <span class="field">
                  <input
                    id="unit-email"
                    type="email"
                    v-model="unitLead.email"
                    aria-required="true"
                    :aria-invalid="String(!!unitLeadErrors.email)"
                    :aria-describedby="unitLeadErrors.email ? 'err-unit-email' : null"
                    @blur="touchUnitField('email')"
                  />
                </span>
                <span v-if="unitLeadErrors.email" id="err-unit-email" class="sp-error" role="alert">{{ unitLeadErrors.email }}</span>
              </li>

              <li class="sbm_area sp-field">
                <UiButton
                  type="submit"
                  variant="secondary"
                  size="lg"
                  :block="true"
                  :loading="unitLeadLoading"
                >
                  שליחה
                </UiButton>
              </li>
            </ul>
          </form>
        </div>

        <!-- ---- unit-member lead success sub-view ---- -->
        <div v-else key="unitSuccess" class="sp-unit-success">
          <div class="sp-unit-success__icon" aria-hidden="true">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>
          <h2 class="sp-unit-success__title">תודה!</h2>
          <p class="sp-unit-success__text">ניצור איתך קשר בהקדם.</p>
          <button type="button" class="sp-back-link" @click="backToPlans">
            <span class="sp-back-link__icon" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </span>
            חזרה למסלולים
          </button>
        </div>

      </transition>
    </div>
    <!-- end step 2 -->

    <!-- Payment Modal -->
    <div v-if="showPaymentIframe" class="sp-payment-modal">
      <!-- Overlay -->
      <div class="sp-payment-modal__overlay" @click="closePaymentModal"></div>
      
      <!-- Modal Panel -->
      <div class="sp-payment-modal__panel">
        <!-- Close button -->
        <button
          type="button"
          class="sp-payment-modal__close"
          @click="closePaymentModal"
          aria-label="סגור"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        
        <!-- Payment content -->
        <div class="sp-payment-modal__content">
          <payment-tranzila
            :pending_token="pendingToken"
            :sum="paymentSum"
            :plan_id="paymentPlanId"
            @payment_done="onPaymentDone"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import UiButton        from '../ui/Button.vue';
import PaymentTranzila from '../payment_tranzila.vue';

// Honest, top-banner messages for server-side (not user-data) failures.
// These are distinct from field-validation codes: they mean the server had
// a real problem (DB/exception), not that the user's input was wrong.
const SERVER_ISSUE_MESSAGES = {
  stage_db_error: 'קרתה תקלה זמנית בשרת בשמירת הפרטים שלך. נסה/י שוב בעוד רגע, ואם הבעיה חוזרת פני/ה לתמיכה.',
  validate_exception: 'קרתה תקלה זמנית בבדיקת הפרטים מול השרת. נסה/י שוב בעוד רגע.',
  stage_exception: 'קרתה תקלה זמנית בשרת בביצוע ההרשמה. נסה/י שוב, ואם הבעיה חוזרת פני/ה לתמיכה.',
};
const SERVER_ISSUE_CODES = Object.keys(SERVER_ISSUE_MESSAGES);

export default {
  name: 'SignupPanel',
  components: { UiButton, PaymentTranzila },

  props: {
    initialStep: { type: Number, default: 1 },
    initialPlanId: { type: [Number, String], default: 0 },
  },

  data() {
    return {
      signupStep: this.initialStep || 1,
      signup: {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        password: '',
        accept_terms: false,
        accept_adv: false,
      },
      signupErrors: {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        password: '',
        terms: '',
      },
      signupTouched: {},
      signupSubmitAttempted: false,
      signupLoading: false,
      plansLoading: false,
      plansFetchError: false,
      signupBanner: '',
      showPassword: false,
      selectedPlan: (this.initialPlanId && Number(this.initialPlanId) > 0) ? Number(this.initialPlanId) : null,
      plans: [],
      // pending_token identifies a staged (not-yet-created) account. No user_token
      // and no session exist until check_payment returns done:true.
      pendingToken: '',
      showPaymentIframe: false,
      paymentSum: 0,
      paymentPlanId: 0,

      // Step-2 sub-view switch: plan picker vs. the "unit member" lead form.
      step2View: 'plans',
      unitLead: { name: '', phone: '', email: '' },
      unitLeadErrors: { name: '', phone: '', email: '' },
      unitLeadTouched: {},
      unitLeadSubmitAttempted: false,
      unitLeadLoading: false,
      unitLeadBanner: '',
    };
  },

  computed: {
    isStep1Valid() {
      return (
        this.signup.first_name.trim().length > 0 &&
        this.signup.last_name.trim().length > 0 &&
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(this.signup.email) &&
        this.signup.phone.replace(/\D/g, '').length >= 9 &&
        this.signup.password.length >= 6 &&
        this.signup.accept_terms
      );
    },
  },

  mounted() {
    // No resume-from-storage: every visit to the signup panel starts clean at
    // step 1. Leaving mid-signup (even mid-payment) and coming back means
    // starting over — no account exists until a payment actually completes
    // (see onPaymentDone), so there is nothing to strand or protect here.
    if (this.signupStep === 2) {
      this.retryPlansFetch();
    }
  },

  methods: {
    focusFirstStep1Error() {
      const order = [
        { key: 'first_name', id: 'signup-first_name' },
        { key: 'last_name', id: 'signup-last_name' },
        { key: 'email', id: 'signup-email' },
        { key: 'phone', id: 'signup-phone' },
        { key: 'password', id: 'signup-password' },
        { key: 'terms', id: 'signup-terms' },
      ];
      const first = order.find(({ key }) => !!this.signupErrors[key]);
      if (!first) return;
      this.$nextTick(() => {
        const el = document.getElementById(first.id);
        if (el) el.focus();
      });
    },

    extractPlansFromResponse(resp) {
      if (!resp) return [];
      const data = resp.data || {};
      const body = data.data || {};
      return this.normalizePlans(body.data || body.plans || body.items || body);
    },

    fallbackPlansFromStore() {
      const rows = this.$store && this.$store.state && this.$store.state.restrictions_plans;
      if (!Array.isArray(rows)) return [];
      return rows
        .filter((r) => r && Number(r.id) > 0 && Number(r.price) > 0)
        .map((r) => ({
          id: Number(r.id),
          name: r.name || 'מסלול',
          price: Number(r.price || 0),
          discount_price: Number(r.price || 0),
          discount_months: 0,
          free_days: 0,
          period: Number(r.subscription_days || 30) >= 365 ? 'yearly' : 'monthly',
        }));
    },

    touchField(field) {
      this.$set(this.signupTouched, field, true);
      this.validateField(field);
    },

    validateField(field) {
      if (!this.signupTouched[field] && !this.signupSubmitAttempted) return;
      const s = this.signup;
      const email = (s.email || '').trim();
      const phoneDigits = (s.phone || '').replace(/\D/g, '');
      switch (field) {
        case 'first_name':
          this.signupErrors.first_name = s.first_name.trim() ? '' : 'שדה חובה';
          break;
        case 'last_name':
          this.signupErrors.last_name = s.last_name.trim() ? '' : 'שדה חובה';
          break;
        case 'email':
          this.signupErrors.email = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email)
            ? ''
            : 'כתובת מייל לא תקינה';
          break;
        case 'phone':
          this.signupErrors.phone = phoneDigits.length >= 9
            ? ''
            : 'מספר טלפון לא תקין';
          break;
        case 'password':
          this.signupErrors.password = s.password.length >= 6
            ? ''
            : 'הסיסמה חייבת להכיל לפחות 6 תווים';
          break;
        case 'terms':
          this.signupErrors.terms = s.accept_terms ? '' : 'יש לאשר את תנאי השימוש';
          break;
      }
    },

    validateAllStep1() {
      this.signupSubmitAttempted = true;
      ['first_name', 'last_name', 'email', 'phone', 'password', 'terms'].forEach((f) => {
        this.$set(this.signupTouched, f, true);
        this.validateField(f);
      });
      return (
        !this.signupErrors.first_name &&
        !this.signupErrors.last_name &&
        !this.signupErrors.email &&
        !this.signupErrors.phone &&
        !this.signupErrors.password &&
        !this.signupErrors.terms
      );
    },

    applyStep1ServerErrors(errors = []) {
      if (!Array.isArray(errors) || errors.length === 0) return;

      if (errors.indexOf('first_name_required') !== -1) {
        this.signupErrors.first_name = 'שדה חובה';
        this.$set(this.signupTouched, 'first_name', true);
      }
      if (errors.indexOf('last_name_required') !== -1) {
        this.signupErrors.last_name = 'שדה חובה';
        this.$set(this.signupTouched, 'last_name', true);
      }
      if (errors.indexOf('email_invalid') !== -1) {
        this.signupErrors.email = 'כתובת מייל לא תקינה';
        this.$set(this.signupTouched, 'email', true);
      }
      if (errors.indexOf('phone_invalid') !== -1) {
        this.signupErrors.phone = 'מספר טלפון לא תקין';
        this.$set(this.signupTouched, 'phone', true);
      }
      if (errors.indexOf('password_too_short') !== -1) {
        this.signupErrors.password = 'הסיסמה חייבת להכיל לפחות 6 תווים';
        this.$set(this.signupTouched, 'password', true);
      }
      if (errors.indexOf('terms_required') !== -1) {
        this.signupErrors.terms = 'יש לאשר את תנאי השימוש';
        this.$set(this.signupTouched, 'terms', true);
      }
      if (errors.indexOf('email_taken') !== -1) {
        this.signupErrors.email = 'כתובת המייל כבר רשומה במערכת.';
        this.$set(this.signupTouched, 'email', true);
        this.$emit('email-taken', this.signup.email);
      }
      if (errors.indexOf('phone_taken') !== -1) {
        this.signupErrors.phone = 'מספר זה כבר רשום.';
        this.$set(this.signupTouched, 'phone', true);
      }
    },

    step1BannerFromErrors(errors = []) {
      if (!Array.isArray(errors) || errors.length === 0) return '';
      if (errors.indexOf('email_taken') !== -1) {
        return 'כתובת המייל כבר קיימת במערכת. אפשר להתחבר או להשתמש בכתובת אחרת.';
      }
      if (errors.indexOf('phone_taken') !== -1) {
        return 'מספר הטלפון כבר קיים במערכת. אפשר להתחבר או להשתמש במספר אחר.';
      }
      if (errors.indexOf('terms_required') !== -1) {
        return 'יש לאשר את תנאי השימוש כדי להמשיך.';
      }
      if (errors.indexOf('password_too_short') !== -1) {
        return 'הסיסמה חייבת להכיל לפחות 6 תווים.';
      }
      if (errors.indexOf('email_invalid') !== -1) {
        return 'כתובת המייל שהוזנה אינה תקינה.';
      }
      if (errors.indexOf('phone_invalid') !== -1) {
        return 'מספר הטלפון שהוזן אינו תקין.';
      }
      if (errors.indexOf('first_name_required') !== -1 || errors.indexOf('last_name_required') !== -1) {
        return 'יש להשלים שם פרטי ושם משפחה כדי להמשיך.';
      }
      const serverIssueCode = SERVER_ISSUE_CODES.find((code) => errors.indexOf(code) !== -1);
      if (serverIssueCode) {
        return SERVER_ISSUE_MESSAGES[serverIssueCode];
      }
      if (this.hasStep1FieldErrors()) {
        return 'יש לתקן את השדות המסומנים לפני המשך.';
      }
      // Unrecognized server error code: never fail silently — show an honest
      // message and surface the code itself so it's traceable to support/logs.
      return 'אירעה תקלה בצד השרת (קוד: ' + errors.join(', ') + '). נסה/י שוב, ואם הבעיה נמשכת פני/ה לתמיכה.';
    },

    hasStep1FieldErrors() {
      return [
        this.signupErrors.first_name,
        this.signupErrors.last_name,
        this.signupErrors.email,
        this.signupErrors.phone,
        this.signupErrors.password,
        this.signupErrors.terms,
      ].some(Boolean);
    },

    validateStep1WithServer() {
      // IMPORTANT: api_call()'s returned promise only resolves with the real
      // axios response when an `onComplete` callback is supplied (see
      // main.js — without it, the response is swallowed and the chain
      // resolves to `undefined`). Use `onComplete` + an explicit Promise so
      // server error codes are actually readable, and so `.catch()` below
      // only fires on a true network/HTTP failure — never on a well-formed
      // `{success:false, errors:[...]}` response.
      return new Promise((resolve) => {
        this.api_call({
          url: 'validate_signup_step1',
          data: {
            first_name: this.signup.first_name,
            last_name: this.signup.last_name,
            email: this.signup.email,
            phone: this.signup.phone,
            password: this.signup.password,
            accept_terms: this.signup.accept_terms,
          },
          onComplete: (resp) => {
            const d = resp && resp.data && resp.data.data;
            const isSuccess = !!(d && d.success);
            const errors = (d && Array.isArray(d.errors)) ? d.errors : [];
            if (isSuccess) {
              resolve(true);
              return;
            }
            this.applyStep1ServerErrors(errors);
            this.signupBanner = this.step1BannerFromErrors(errors);
            resolve(false);
          },
        }).catch(() => {
          this.signupBanner = 'לא הצלחנו לאמת את הפרטים כרגע. נסה/י שוב.';
          resolve(false);
        });
      });
    },

    // Step 1 submit — NO account is created here. Only validate the form and
    // load the plan list, then advance to the plan picker. Staging (api/stage_signup)
    // happens in submitSignup, once a paid plan_id is known (the backend rejects
    // plan_id 0 — there is no free tier to stage against).
    async advanceToStep2() {
      if (!this.validateAllStep1()) {
        this.signupBanner = 'יש להשלים את כל שדות החובה לפני המשך.';
        this.focusFirstStep1Error();
        return;
      }

      this.signupBanner = '';
      this.plansLoading = true;
      this.plansFetchError = false;

      const serverValid = await this.validateStep1WithServer();
      if (!serverValid) {
        this.plansLoading = false;
        if (!this.signupBanner) {
          this.signupBanner = this.hasStep1FieldErrors()
            ? 'יש לתקן את השדות המסומנים לפני המשך.'
            : 'לא הצלחנו לאמת את הפרטים. בדקי שהמייל והטלפון תקינים ונסי שוב.';
        }
        if (this.hasStep1FieldErrors()) {
          this.focusFirstStep1Error();
        }
        return;
      }

      const onPlansLoaded = (resp) => {
        this.plansLoading = false;
        this.plans = this.extractPlansFromResponse(resp);
        if (this.plans.length === 0) {
          this.plans = this.fallbackPlansFromStore();
        }
        this.plansFetchError = this.plans.length === 0;
        this.preselectYearlyPlan();
        this.signupStep = 2;
      };

      this.api_call({
        url: 'get_plans',
        onComplete: onPlansLoaded,
      }).catch(() => {
        // Fallback for environments where get_plans is unavailable.
        this.api_call({
          url: 'subscription/plans',
          onComplete: onPlansLoaded,
        }).catch(() => {
          this.plansLoading = false;
          this.plans = this.fallbackPlansFromStore();
          this.plansFetchError = this.plans.length === 0;
          this.preselectYearlyPlan();
          this.signupStep = 2;
        });
      });
    },

    // Step 2 — stage the signup (create no account yet) with the chosen plan,
    // then hand the resulting pending_token to <payment-tranzila> to start payment.
    submitSignup() {
      if (!this.validateAllStep1()) {
        this.signupStep = 1;
        this.signupBanner = 'יש להשלים את כל הפרטים בשלב הראשון לפני המשך לתשלום.';
        this.focusFirstStep1Error();
        return;
      }

      if (this.selectedPlan === null || this.selectedPlan === undefined || this.selectedPlan === '') {
        this.signupBanner = 'יש לבחור חבילה לפני המשך.';
        return;
      }

      this.signupBanner = '';
      this.signupLoading = true;

      const planId = Number(this.selectedPlan);
      const plan = this.plans.find(p => Number(p.id) === planId);

      this.api_call({
        url: 'stage_signup',
        data: {
          first_name: this.signup.first_name,
          last_name: this.signup.last_name,
          email: this.signup.email,
          phone: this.signup.phone,
          password: this.signup.password,
          accept_terms: this.signup.accept_terms,
          accept_adv: this.signup.accept_adv,
          plan_id: planId,
          source: 'in_app',
          utm_source: localStorage.getItem('utm_source') || '',
          utm_medium: localStorage.getItem('utm_medium') || '',
          utm_campaign: localStorage.getItem('utm_campaign') || '',
        },
        onComplete: (resp) => {
          this.signupLoading = false;
          const d = resp && resp.data && resp.data.data;
          if (d && d.success && d.pending_token) {
            this.pendingToken = d.pending_token;
            this.paymentSum = plan ? this.effectivePrice(plan) : 0;
            this.paymentPlanId = planId > 0 ? planId : 0;
            this.showPaymentIframe = true;
            return;
          }
          const errors = (d && d.errors) || [];
          const hasStep1Errors = [
            'first_name_required',
            'last_name_required',
            'email_invalid',
            'password_too_short',
            'terms_required',
            'phone_invalid',
            'email_taken',
            'phone_taken',
          ].some((code) => errors.indexOf(code) !== -1);

          if (errors.indexOf('first_name_required') !== -1) {
            this.signupErrors.first_name = 'שדה חובה';
            this.$set(this.signupTouched, 'first_name', true);
          }
          if (errors.indexOf('last_name_required') !== -1) {
            this.signupErrors.last_name = 'שדה חובה';
            this.$set(this.signupTouched, 'last_name', true);
          }
          if (errors.indexOf('email_invalid') !== -1) {
            this.signupErrors.email = 'כתובת מייל לא תקינה';
            this.$set(this.signupTouched, 'email', true);
          }
          if (errors.indexOf('password_too_short') !== -1) {
            this.signupErrors.password = 'הסיסמה חייבת להכיל לפחות 6 תווים';
            this.$set(this.signupTouched, 'password', true);
          }
          if (errors.indexOf('terms_required') !== -1) {
            this.signupErrors.terms = 'יש לאשר את תנאי השימוש';
            this.$set(this.signupTouched, 'terms', true);
          }
          if (errors.indexOf('phone_invalid') !== -1) {
            this.signupErrors.phone = 'מספר טלפון לא תקין';
            this.$set(this.signupTouched, 'phone', true);
          }

          if (errors.indexOf('email_taken') !== -1) {
            this.$emit('email-taken', this.signup.email);
            this.signupBanner = 'כתובת המייל כבר רשומה במערכת.';
            this.signupStep = 1;
          } else if (errors.indexOf('phone_taken') !== -1) {
            this.signupErrors.phone = 'מספר זה כבר רשום.';
            this.$set(this.signupTouched, 'phone', true);
            this.signupStep = 1;
            this.$nextTick(() => {
              const el = document.getElementById('signup-phone');
              if (el) el.focus();
            });
          } else if (hasStep1Errors) {
            this.signupStep = 1;
            this.signupBanner = 'יש לתקן את השדות המסומנים בשלב הראשון.';
            this.focusFirstStep1Error();
          } else if (errors.indexOf('plan_required') !== -1 || errors.indexOf('plan_invalid') !== -1) {
            this.signupBanner = 'יש לבחור מסלול בתשלום כדי להמשיך.';
          } else if (SERVER_ISSUE_CODES.some((code) => errors.indexOf(code) !== -1)) {
            // Real server/DB-side failure (not the user's data) — say so honestly.
            const serverIssueCode = SERVER_ISSUE_CODES.find((code) => errors.indexOf(code) !== -1);
            this.signupBanner = SERVER_ISSUE_MESSAGES[serverIssueCode];
          } else if (errors.length) {
            // Unrecognized server error code: never fail silently — show an
            // honest message and surface the code so it's traceable.
            this.signupBanner = 'אירעה שגיאה בהרשמה (קוד: ' + errors.join(', ') + '). נסה/י שוב, ואם הבעיה נמשכת פני/ה לתמיכה.';
          } else {
            this.signupBanner = 'אירעה שגיאה. נסה/י שוב.';
          }
        },
      }).catch(() => {
        this.signupLoading = false;
        this.signupBanner = 'אירעה שגיאה בחיבור לשרת. נסה/י שוב.';
      });
    },

    // check_payment (via <payment-tranzila>) reports done:true only once the
    // account has actually been created server-side, handing back a fresh
    // user_token. That is the ONLY point at which we store a session.
    onPaymentDone(success = true, userToken = '') {
      this.showPaymentIframe = false;
      if (!success || !userToken) {
        this.signupBanner = 'התשלום לא הושלם. בחר/י חבילה או נסה/י שוב.';
        this.signupStep = 2;
        return;
      }
      localStorage.setItem('user_token', userToken);
      document.location = '/';
    },

    closePaymentModal() {
      // User closed/cancelled payment - stay on step 2, keep plan selected.
      // No account/session was ever created, so there is nothing to roll back.
      this.showPaymentIframe = false;
      this.signupBanner = 'התשלום לא הושלם. ניתן לבחור חבילה אחרת או לנסות שוב.';
      this.signupStep = 2;
    },

    // Helper: effective monthly price (discount_price if discount active, else regular)
    effectivePrice(plan) {
      return Number(plan.discount_months) > 0 ? Number(plan.discount_price || plan.price || 0) : Number(plan.price || 0);
    },

    // Helper: % savings of the yearly plan vs. the monthly plan's price,
    // comparing the yearly plan's monthly-equivalent cost (price/12).
    yearlySavingsPct(plan) {
      if (!plan || plan.period !== 'yearly') return 0;
      const yearlyPrice = this.effectivePrice(plan);
      if (!(yearlyPrice > 0)) return 0;
      const monthlyPlan = this.plans.find(p => p.period === 'monthly');
      if (!monthlyPlan) return 0;
      const monthlyPrice = this.effectivePrice(monthlyPlan);
      if (!(monthlyPrice > 0)) return 0;
      const yearlyMonthlyEquivalent = yearlyPrice / 12;
      const pct = Math.round((1 - (yearlyMonthlyEquivalent / monthlyPrice)) * 100);
      return pct > 0 ? pct : 0;
    },

    // Pre-select the Yearly plan once plans have loaded, unless a plan is
    // already selected (e.g. via initialPlanId prop).
    preselectYearlyPlan() {
      if (this.selectedPlan !== null && this.selectedPlan !== undefined && this.selectedPlan !== '') return;
      const yearlyPlan = this.plans.find(p => p.period === 'yearly');
      if (yearlyPlan) this.selectedPlan = yearlyPlan.id;
    },

    // ---- "For unit members" lead form (step2View sub-view) ----
    // No account and no payment are created here — this only calls api/unit_lead.
    openUnitMemberForm() {
      this.unitLead = { name: '', phone: '', email: '' };
      this.unitLeadErrors = { name: '', phone: '', email: '' };
      this.unitLeadTouched = {};
      this.unitLeadSubmitAttempted = false;
      this.unitLeadBanner = '';
      this.step2View = 'unitForm';
      this.$nextTick(() => {
        const el = document.getElementById('unit-name');
        if (el) el.focus();
      });
    },

    backToPlans() {
      this.step2View = 'plans';
    },

    touchUnitField(field) {
      this.$set(this.unitLeadTouched, field, true);
      this.validateUnitField(field);
    },

    validateUnitField(field) {
      if (!this.unitLeadTouched[field] && !this.unitLeadSubmitAttempted) return;
      switch (field) {
        case 'name':
          this.unitLeadErrors.name = this.unitLead.name.trim() ? '' : 'שדה חובה';
          break;
        case 'phone':
          this.unitLeadErrors.phone = this.unitLead.phone.replace(/\D/g, '').length >= 9
            ? ''
            : 'מספר טלפון לא תקין';
          break;
        case 'email':
          this.unitLeadErrors.email = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(this.unitLead.email)
            ? ''
            : 'כתובת מייל לא תקינה';
          break;
      }
    },

    validateAllUnitLead() {
      this.unitLeadSubmitAttempted = true;
      ['name', 'phone', 'email'].forEach((f) => {
        this.$set(this.unitLeadTouched, f, true);
        this.validateUnitField(f);
      });
      return !this.unitLeadErrors.name && !this.unitLeadErrors.phone && !this.unitLeadErrors.email;
    },

    submitUnitLead() {
      if (!this.validateAllUnitLead()) return;
      this.unitLeadBanner = '';
      this.unitLeadLoading = true;
      this.api_call({
        url: 'unit_lead',
        data: {
          name: this.unitLead.name,
          phone: this.unitLead.phone,
          email: this.unitLead.email,
        },
        onComplete: (resp) => {
          this.unitLeadLoading = false;
          const d = resp && resp.data && resp.data.data;
          if (d && d.success) {
            this.step2View = 'unitSuccess';
            return;
          }
          this.unitLeadBanner = 'אירעה שגיאה בשליחת הפרטים. נסה/י שוב.';
        },
      }).catch(() => {
        this.unitLeadLoading = false;
        this.unitLeadBanner = 'אירעה שגיאה בחיבור לשרת. נסה/י שוב.';
      });
    },

    // Helper: period-aware billing label. Plans without a `period` (e.g. the
    // fallback/free plans) render no label rather than assuming monthly.
    planPeriodLabel(plan) {
      // A free plan has no billing cadence to show, regardless of what
      // `period` the DB row carries (ENUM defaults to 'monthly').
      const price = plan && (Number(plan.discount_months) > 0 ? plan.discount_price : plan.price);
      if (!plan || !plan.period || !(Number(price) > 0)) return '';
      if (plan.period === 'yearly') return 'לשנה';
      if (plan.period === 'monthly') return 'לחודש';
      return '';
    },

    // Helper: generate benefit bullets from plan data
    planBenefits(plan) {
      const benefits = [];
      if (plan.free_days > 0) {
        benefits.push(`ניסיון חינם ל-${plan.free_days} ימים`);
      }
      if (plan.discount_months > 0) {
        benefits.push(`מחיר מוזל ל-${plan.discount_months} חודשים ראשונים`);
      }
      benefits.push('גישה מלאה לכל התכנים');
      benefits.push('צפייה בכל מכשיר');
      return benefits;
    },

    normalizePlans(raw) {
      let list = [];
      if (!raw) return list;
      if (Array.isArray(raw)) list = raw;
      else if (Array.isArray(raw.items)) list = raw.items;
      else if (Array.isArray(raw.plans)) list = raw.plans;
      else if (typeof raw === 'object') list = Object.values(raw);

      return list
        .filter((p) => p && Number(p.id || p.plan_id) > 0)
        .map((p) => {
          const basePrice = Number(p.price || p.monthly_price || p.amount || 0);
          const discountPrice = Number(p.discount_price || p.discounted_price || p.sale_price || basePrice || 0);
          return {
            id: Number(p.id || p.plan_id),
            name: p.name || p.title || p.plan_name || 'מסלול',
            price: Number.isFinite(basePrice) ? basePrice : 0,
            discount_price: Number.isFinite(discountPrice) ? discountPrice : 0,
            discount_months: Number(p.discount_months || p.discount_duration || p.sale_months || 0),
            free_days: Number(p.free_days || p.trial_days || p.free_trial_days || 0),
            period: p.period || '',
          };
        })
        // Signup offers only the two recurring plans — no free tier, no other periods.
        .filter((p) => (p.period === 'monthly' || p.period === 'yearly') && p.price > 0);
    },

    retryPlansFetch() {
      this.plansLoading = true;
      this.plansFetchError = false;
      this.api_call({
        url: 'get_plans',
        onComplete: (resp) => {
          this.plansLoading = false;
          this.plans = this.extractPlansFromResponse(resp);
          if (this.plans.length === 0) {
            this.plans = this.fallbackPlansFromStore();
          }
          this.plansFetchError = this.plans.length === 0;
          this.preselectYearlyPlan();
        },
      }).catch(() => {
        this.plansLoading = false;
        this.plans = this.fallbackPlansFromStore();
        this.plansFetchError = this.plans.length === 0;
        this.preselectYearlyPlan();
      });
    },
  },

  watch: {
    showPaymentIframe(isOpen) {
      // Prevent body scroll when modal is open
      if (isOpen) {
        document.body.classList.add('modal-open');
      } else {
        document.body.classList.remove('modal-open');
      }
    },
  },

  beforeDestroy() {
    // Cleanup: ensure modal-open class is removed when component is destroyed
    document.body.classList.remove('modal-open');
  },
};
</script>

